import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GroupProvider, useGroup } from './contexts/GroupContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SavedViewsProvider } from './contexts/SavedViewsContext';
import { ToastProvider } from './components/common';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Logout from './pages/Logout';
import DeveloperDashboard from './pages/DeveloperDashboard';
import PortalLayout from './components/dev/PortalLayout';
import NotFound from './components/dev/NotFound';
import ReleasesPage from './pages/ReleasesPage';
import BillingPage from './pages/BillingPage';
import DocumentationPage from './pages/DocumentationPage';
import SchemasPage from './pages/SchemasPage';
import ApplicationsPage from './pages/ApplicationsPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import PluginsList from './pages/plugins/PluginsList';
import PluginCreate from './pages/plugins/PluginCreate';
import PluginWorkspace from './pages/plugins/PluginWorkspace';
import DeveloperHome from './pages/DeveloperHome';
import CliPage from './pages/CliPage';
import CreateFlow from './pages/create/CreateFlow';
import TokensPage from './pages/TokensPage';
import Welcome from './pages/welcome/Welcome';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/App.css';

// Gate for the developer portal. Requires a logged-in account with app access.
function ProtectedRoute({ children }) {
  const { user, loading, hasAccess } = useAuth();

  // Only a COLD boot (no cached identity) ever waits here now, and it waits
  // on bare stage — the "Loading…" wall is retired (the nucleus fix).
  if (loading) {
    return null;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!hasAccess('app')) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center', fontFamily: 'var(--font-mono, monospace)' }}>
        <h2 style={{ marginBottom: '1rem' }}>Access Denied</h2>
        <p style={{ opacity: 0.6 }}>Your account does not have developer access. Contact an administrator.</p>
      </div>
    );
  }
  return children;
}

// The dashboard, scoped to the active identity/group context. Remounts on
// context switch so all dashboard state (JWT, apps, schemas) reloads cleanly.
function DeveloperRoute({ tab, mode, type }) {
  const { activeGroupId } = useGroup();
  return (
    <PortalLayout>
      <DeveloperDashboard key={activeGroupId || 'personal'} groupId={activeGroupId} initialTab={tab} initialMode={mode} initialType={type} />
    </PortalLayout>
  );
}

// Same dashboard, but inside the in-app portal shell (floating sidebar, no top
// bar) — used for the "logged-in app" surfaces like Build → Create.
function PortalRoute({ tab, mode, type }) {
  const { activeGroupId } = useGroup();
  return (
    <PortalLayout>
      <DeveloperDashboard key={activeGroupId || 'personal'} groupId={activeGroupId} initialTab={tab} initialMode={mode} initialType={type} />
    </PortalLayout>
  );
}

// Legacy-redirect helpers: carry the query string / the :appId param across.
function RedirectWithSearch({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}
function RedirectAppDetail() {
  const { appId } = useParams();
  return <Navigate to={`/applications/${appId}`} replace />;
}

function RedirectPluginEdit() {
  const { pluginId } = useParams();
  return <Navigate to={`/plugins/${pluginId}`} replace />;
}

// An address the portal does not have. Signed in, it is a page inside the frame
// that says so — a mistyped /apps used to land on the signed-out front door and
// read as being logged out. Signed out, it is still the front door.
function UnknownRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  return <PortalLayout><NotFound /></PortalLayout>;
}

// Auth pages redirect into the dashboard once a session exists.
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return null;
  }
  if (user) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AuthProvider>
            <GroupProvider>
              <NotificationProvider>
                <SavedViewsProvider>
                  <div className="app">
                    <Routes>
                      {/* Public marketing landing — the front door to the developer ecosystem. */}
                      <Route path="/" element={<Landing />} />

                      {/* Auth (shared workflow with the consumer app). */}
                      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                      <Route path="/logout" element={<Logout />} />

                      {/* Documentation — a page OF the portal (same shell, same
                          sidebar), and PUBLIC.

                          It was behind ProtectedRoute, so a developer could not
                          read one sentence about Paradigm without first creating
                          an account — while the backend served the very same
                          markdown to anyone (GET /docs/guides is unauthenticated
                          by design). The wall was the SPA's alone, and it also
                          kept the docs out of every search index.

                          PortalLayout renders for a guest: EcoShell passes
                          `currentUser: null` and the kit's rail degrades. */}
                      <Route
                        path="/documentation/:slug?"
                        element={
                          <PortalLayout>
                            <DocumentationPage />
                          </PortalLayout>
                        }
                      />

                      {/* The portal's home — the page behind the column's De tile. */}
                      <Route path="/home" element={<ProtectedRoute><PortalLayout><DeveloperHome /></PortalLayout></ProtectedRoute>} />

                      {/* First run — no frame. Home sends an empty space here
                          once; every rail door would open onto an empty page. */}
                      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

                      {/* Schemas — in-app portal shell (sidebar, no top bar). */}
                      <Route
                        path="/schemas"
                        element={
                          <ProtectedRoute>
                            <PortalLayout><SchemasPage /></PortalLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/schemas/explore" element={<ProtectedRoute><PortalLayout><SchemasPage /></PortalLayout></ProtectedRoute>} />
                      <Route path="/schemas/create" element={<ProtectedRoute><PortalLayout><SchemasPage /></PortalLayout></ProtectedRoute>} />

                      {/* THE APPLICATIONS SECTION — four pages under one nav.
                          Manage Applications is the front: the door said
                          Applications and used to deliver three charts, while
                          the list it was named after sat a click deeper.

                          The analytics keep every section they had; they are
                          siblings of the list now rather than its landing page,
                          reached by the leaf COLUMN that carries all four —
                          the same hub grammar Schemas and Develop speak, not a
                          strip across the head. The bespoke "see the metrics ->"
                          and "<- back" links that stood in for it are gone.

                          Static segments outrank the :appId param in React
                          Router's own ranking, so no app can shadow them. */}
                      <Route path="/applications" element={<ProtectedRoute><PortalLayout><MyApplicationsPage /></PortalLayout></ProtectedRoute>} />
                      <Route path="/applications/metrics" element={<ProtectedRoute><PortalLayout><ApplicationsPage section="usage" /></PortalLayout></ProtectedRoute>} />
                      <Route path="/applications/os-evals" element={<ProtectedRoute><PortalLayout><ApplicationsPage section="scores" /></PortalLayout></ProtectedRoute>} />
                      <Route path="/applications/connections" element={<ProtectedRoute><PortalLayout><ApplicationsPage section="connections" /></PortalLayout></ProtectedRoute>} />
                      {/* Former addresses. /insights existed for one iteration;
                          /all for as long as the overview held the front. */}
                      <Route path="/applications/all" element={<Navigate to="/applications" replace />} />
                      <Route path="/applications/insights" element={<Navigate to="/applications/metrics" replace />} />
                      <Route path="/applications/:appId" element={<ProtectedRoute><PortalLayout><MyApplicationsPage /></PortalLayout></ProtectedRoute>} />
                      {/* Start developing — two lanes, opening on whichever fits.
                          It used to be a tab of the dashboard with three lanes and a
                          fork; "Tokens" was never a lane (it is step one of starting)
                          and nothing addressed picking an app back up at all. */}
                      <Route path="/create" element={<ProtectedRoute><PortalLayout><CreateFlow /></PortalLayout></ProtectedRoute>} />
                      <Route path="/api-docs" element={<ProtectedRoute><PortalRoute tab="capabilities" /></ProtectedRoute>} />

                      {/* THE TOKEN LIST HAS A DOOR AGAIN. Folding Tokens into
                          "Start developing" as a mode took the lane away and the
                          address with it: /tokens redirected to the MINT flow, so
                          the only way to see what you already hold — previews,
                          expiries, last-used, Revoke — was a query string nothing
                          linked. Develop mints; this page is the ledger. It is
                          still not a lane and still not a sidebar cell (a
                          credential is step one of starting, not a peer of
                          Applications); the Develop page links here. */}
                      <Route path="/tokens" element={<ProtectedRoute><PortalLayout><TokensPage /></PortalLayout></ProtectedRoute>} />

                      {/* Legacy redirects — the old /build/* scheme (renamed 2026-08-14). */}
                      <Route path="/build" element={<Navigate to="/applications" replace />} />
                      <Route path="/build/applications" element={<Navigate to="/applications" replace />} />
                      <Route path="/build/applications/:appId" element={<RedirectAppDetail />} />
                      <Route path="/build/create" element={<RedirectWithSearch to="/create" />} />
                      <Route path="/build/cli" element={<Navigate to="/create?lane=new" replace />} />
                      <Route path="/build/api-docs" element={<Navigate to="/api-docs" replace />} />
                      {/* PLUGINS: the list, a new one, and one workspace per plugin
                          (Details, Tools, Preview, Test, Verification, Publish, Runs,
                          Settings). /plugins/:id/edit was the old builder's address. */}
                      <Route path="/plugins" element={<ProtectedRoute><PortalLayout><PluginsList /></PortalLayout></ProtectedRoute>} />
                      <Route path="/plugins/new" element={<ProtectedRoute><PortalLayout><PluginCreate /></PortalLayout></ProtectedRoute>} />
                      <Route path="/plugins/:pluginId" element={<ProtectedRoute><PortalLayout><PluginWorkspace /></PortalLayout></ProtectedRoute>} />
                      <Route path="/plugins/:pluginId/edit" element={<RedirectPluginEdit />} />

                      {/* Payouts belong to the developer, not to an app, so this is a
                          portal-level page. An app's Pricing tab links here. */}
                      <Route path="/billing" element={<ProtectedRoute><PortalLayout><BillingPage /></PortalLayout></ProtectedRoute>} />
                      <Route path="/releases" element={<ProtectedRoute><PortalLayout><ReleasesPage /></PortalLayout></ProtectedRoute>} />
                      <Route path="/cli" element={<ProtectedRoute><PortalLayout><CliPage /></PortalLayout></ProtectedRoute>} />

                      {/* Developer dashboard (remaining tabs). */}
                      <Route
                        path="/developer"
                        element={
                          <ProtectedRoute>
                            <DeveloperRoute />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="*" element={<UnknownRoute />} />
                    </Routes>
                  </div>
                </SavedViewsProvider>
              </NotificationProvider>
            </GroupProvider>
          </AuthProvider>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
